// Seans ekle and Yeni danışan (Onur, 03.11.2026): the two things the
// day view could not do without a chat, on the pay sheet's card.
// Seans ekle: a danışan found by name among every card (a pasif one
// comes back active with the seans), the danışman, the day and hour,
// once or a program — 50 dk, bireysel; a new danışan × danışman pair
// takes its fee here, the Nefes share by the KB rule (Nefes vakası 50,
// the danışman's own 40, a second danışman always 50, Seda 0). Yeni
// danışan: the card, the veli who gets the WhatsApp confirmations,
// the danışman, the fee and whose vaka; a name already on a card is
// refused, and the way on is that card. Saving goes straight to Seans
// ekle with the new card. The rest (groups, other lengths, program
// edits, TC, consents, faturalı, sabit pay) stays in chat.

import {useEffect, useMemo, useRef, useState} from "react";
import type {ReactNode} from "react";
import {getDeskClients} from "../api/client";
import type {
  AddClientInput, AddClientWire, AddSessionInput, BoardProvider, DeskClient, DeskClients,
} from "../api/contract";
import {MONTH_TR, WEEKDAY_TR, parseTL, trDative, trGenitive, trNumber, weekdayOf} from "../labels";
import {Avatar} from "../ui/Avatar";
import {Icon} from "../ui/Icon";
import {useMoney} from "../ui/blind-context";
import {Sheet} from "./Sheets";
import {errorText} from "./useActions";

// ── Pieces ────────────────────────────────────────────────

// Every card, read once per sheet; the dev demo brings its own.
function useDeskClients(demo?: DeskClients) {
  const [state, setState] = useState<{data: DeskClients | null; error: string | null}>(
    () => ({data: demo ?? null, error: null}));
  useEffect(() => {
    if (demo) return;
    const ctl = new AbortController();
    getDeskClients(ctl.signal).then(
      ({data}) => !ctl.signal.aborted && setState({data, error: null}),
      (e: unknown) => !ctl.signal.aborted && setState({data: null, error: errorText(e)}));
    return () => ctl.abort();
  }, [demo]);
  return state;
}

// "Deniz Arı" and "deniz ari" are the same name to the search.
const FOLD: Record<string, string> = {
  ı: "i", İ: "i", ş: "s", Ş: "s", ğ: "g", Ğ: "g", ü: "u", Ü: "u", ö: "o", Ö: "o", ç: "c", Ç: "c",
};
const fold = (s: string): string =>
  s.replace(/[ıİşŞğĞüÜöÖçÇ]/g, (c) => FOLD[c]).toLowerCase().trim().replace(/\s+/g, " ");

const initials = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0]?.[0] ?? "?";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toLocaleUpperCase("tr");
};

// "4 Kasım Çarşamba"
const dayWords = (date: string): string =>
  `${Number(date.slice(8, 10))} ${MONTH_TR[Number(date.slice(5, 7)) - 1]} ` +
  WEEKDAY_TR[weekdayOf(date)];
const weekdayLow = (date: string): string =>
  WEEKDAY_TR[weekdayOf(date)].toLocaleLowerCase("tr-TR");

function Head({title, back, onBack, onClose}: {
  title: string; back?: string; onBack?: () => void; onClose: () => void;
}) {
  return (
    <header className="sd-head">
      <div className="sd-who">
        {back && onBack ?
          <button className="sd-chip sd-back" onClick={onBack}>
            <Icon name="back" /><span className="sd-ell">{back}</span>
          </button> :
          <h2 className="sd-title">{title}</h2>}
        <span className="spacer" />
        <button className="sd-round" aria-label="Kapat" onClick={onClose}><Icon name="x" /></button>
      </div>
      {back && onBack && <h2 className="sd-title">{title}</h2>}
    </header>
  );
}

function Field({label, hint, dim, children}: {
  label?: string; hint?: string; dim?: boolean; children: ReactNode;
}) {
  return (
    <div className={dim ? "f dim" : "f"}>
      {label && <div className="f-label">{label}{hint && <em>{hint}</em>}</div>}
      {children}
    </div>
  );
}

function Seg<T extends string>({options, value, onChange, two}: {
  options: {value: T; label: string}[]; value: T | null; onChange: (v: T) => void; two?: boolean;
}) {
  return (
    <div className={two ? "pay-methods two" : "pay-methods"} role="group">
      {options.map((o) => (
        <button key={o.value} aria-pressed={o.value === value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

function ProviderChips({providers, value, onChange}: {
  providers: BoardProvider[]; value: string | null; onChange: (id: string) => void;
}) {
  return (
    <div className="f-chips">
      {providers.map((p) => (
        <button key={p.providerId} className={p.providerId === value ? "sd-chip pressed" : "sd-chip"}
          aria-pressed={p.providerId === value} onClick={() => onChange(p.providerId)}>
          <Avatar providerId={p.providerId} name={p.name} photo={p.photo} />{p.name}
        </button>
      ))}
    </div>
  );
}

function FeeInput({value, onChange, autoFocus, onEnter}: {
  value: string; onChange: (v: string) => void; autoFocus?: boolean; onEnter?: () => void;
}) {
  const field = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (autoFocus) field.current?.focus();
  }, [autoFocus]);
  return (
    <label className="f-input num">
      <input ref={field} type="text" inputMode="decimal" autoComplete="off" placeholder="0"
        aria-label="Seans ücreti (TL)" value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={() => {
          const n = parseTL(value);
          if (n !== null) onChange(trNumber(n));
        }}
        onKeyDown={(e) => e.key === "Enter" && onEnter?.()} />
      <span className="unit">TL</span>
    </label>
  );
}

// The two shares of a fee, said in TL: "Merve'ye 2.000 TL, Nefes'e 2.000 TL".
function splitWords(
  feeTL: number | null, rate: number, providerName: string, tl: (n: number) => string,
): string {
  if (feeTL === null) return "";
  const nefes = Math.round(feeTL * rate) / 100;
  return `Seans başına ${trDative(providerName)} ${tl(feeTL - nefes)}, Nefes'e ${tl(nefes)}.`;
}

// Where the desk types after the picked card: the next field.
const focusNext = (el: HTMLElement | null) => el?.focus();

// ── Seans ekle ────────────────────────────────────────────

export interface AddSessionPreset {
  client: DeskClient;
  welcome: boolean; // the card was just opened: the karşılama line
  guardian?: string;
}

const REPEATS: {value: AddSessionInput["repeat"]; label: string}[] = [
  {value: "once", label: "Bir kez"},
  {value: "weekly", label: "Her hafta"},
  {value: "biweekly", label: "2 haftada bir"},
];

export function AddSessionSheet({providers, date, today, preset, demo, busy, onClose,
  onNewClient, onAdd}: {
  providers: BoardProvider[]; // the board's columns: active, with a calendar
  date: string; // the board's day — the default Gün
  today: string;
  preset?: AddSessionPreset;
  demo?: DeskClients;
  busy: string | null;
  onClose: () => void;
  onNewClient: (typed: string) => void;
  onAdd: (input: AddSessionInput, providerName: string) => Promise<boolean>;
}) {
  const {tl} = useMoney();
  const list = useDeskClients(demo);
  const owner = list.data?.ownerProviderId ?? null;
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState<DeskClient | null>(preset?.client ?? null);
  const [providerId, setProviderId] = useState<string | null>(() =>
    preset && providers.some((p) => p.providerId === preset.client.providerId) ?
      preset.client.providerId : null);
  const [gun, setGun] = useState(date < today ? today : date);
  const [saat, setSaat] = useState("");
  const [repeat, setRepeat] = useState<AddSessionInput["repeat"] | null>(null);
  const [fee, setFee] = useState("");
  const [vaka, setVaka] = useState<"nefes" | "own">("nefes");
  const search = useRef<HTMLInputElement>(null);
  const timeField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!picked) search.current?.focus();
  }, [picked]);

  const provider = providers.find((p) => p.providerId === providerId) ?? null;
  const pick = (c: DeskClient) => {
    setPicked(c);
    if (providers.some((p) => p.providerId === c.providerId)) setProviderId(c.providerId);
  };
  const q = fold(query);
  const results = useMemo(() => {
    if (q.length < 2 || !list.data) return [];
    const hit = list.data.clients.filter((c) => fold(c.name).includes(q));
    hit.sort((a, b) => Number(fold(b.name).startsWith(q)) - Number(fold(a.name).startsWith(q)));
    return hit.slice(0, 6);
  }, [list.data, q]);

  // The pair: what the card knows, and what this sheet must ask. A
  // rate the card has stays; a second danışman is always Nefes's vaka.
  const terms = picked && providerId ? picked.terms[providerId] ?? null : null;
  const isOwner = providerId !== null && providerId === owner;
  const isPrimary = !!picked && providerId !== null && picked.providerId === providerId;
  const needFee = !!picked && !!provider && (!terms || terms.feeTL === null);
  const askVaka = isPrimary && !isOwner && (!terms || terms.rate === null);
  const rate = terms?.rate ?? (isOwner ? 0 : isPrimary && vaka === "own" ? 40 : 50);
  const typedTL = parseTL(fee);
  const feeTL = terms?.feeTL ?? (needFee ? typedTL : null);
  const feeBad = needFee && fee.trim() !== "" && typedTL === null;

  const missing = !picked ? "Danışan seçin" : !provider ? "Danışman seçin" :
    needFee && feeTL === null ? "Ücret girin" : !gun ? "Gün seçin" : !saat ? "Saat girin" :
      !repeat ? "Tekrar seçin" : null;
  const running = !!picked && busy === `add-session:${picked.clientId}`;
  const go = missing === null && busy === null;
  const submit = () => {
    if (!go || !picked || !provider || !repeat) return;
    void onAdd({
      clientId: picked.clientId, providerId: provider.providerId, date: gun, time: saat, repeat,
      ...(needFee && feeTL !== null ? {feeTL} : {}),
      ...(askVaka ? {vaka} : {}),
    }, provider.name).then((done) => done && onClose());
  };

  const whenShort = !saat ? "" : repeat === "weekly" ? `her ${weekdayLow(gun)} ${saat}` :
    repeat === "biweekly" ? `iki haftada bir ${weekdayLow(gun)} ${saat}` :
      `${gun === today ? "bugün" : dayWords(gun).replace(/ \S+$/, "")} ${saat}`;
  const repeatNote = !repeat || !provider || !gun || !saat ? "" :
    repeat === "once" ?
      `Yalnız ${gun === today ? "bugün" : dayWords(gun)}, ${trGenitive(provider.name)} ` +
        "takvimine tek seans." :
      `İlk seans ${dayWords(gun)}, sonra ${repeat === "weekly" ? "her" : "iki haftada bir"} ` +
        `${weekdayLow(gun)} ${saat}, ${trGenitive(provider.name)} takviminde.`;
  const pickedSub = picked ? [
    picked.providerName ? `${trGenitive(picked.providerName)} danışanı` : "danışmanı yok",
    ...(picked.status === "inactive" ? ["pasif — seansla aktif olur"] : []),
    ...(preset?.guardian && preset.client.clientId === picked.clientId ?
      [`veli ${preset.guardian}`] : []),
  ].join(" · ") : "";

  return (
    <Sheet className="seans-sheet pay-sheet payout-sheet add-sheet" onClose={onClose}
      head={<Head title="Seans ekle" onClose={onClose} />}>
      {preset?.welcome && picked?.clientId === preset.client.clientId && (
        <p className="pay-ok"><Icon name="check" />
          <span>{trGenitive(preset.client.name)} kartı açıldı. Hoş geldin mesajı ve form
            gönderildi mi?</span>
        </p>
      )}
      <div className="pay-card">
        <div className="pay-main">
          <Field label="Danışan">
            {picked ?
              <div className="f-picked">
                <span className="avatar md" aria-hidden>{initials(picked.name)}</span>
                <div><b>{picked.name}</b><small>{pickedSub}</small></div>
                <button className="sd-chip small" onClick={() => {
                  setPicked(null);
                  setProviderId(null);
                }}>Değiştir</button>
              </div> :
              <>
                <label className="f-input">
                  <Icon name="search" />
                  <input ref={search} type="text" autoComplete="off" placeholder="Ad soyad"
                    aria-label="Danışan ara" value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && results[0]) pick(results[0]);
                    }} />
                </label>
                {list.error && <p className="pay-note bad">{list.error}</p>}
                {q.length >= 2 && (
                  <div className="f-results">
                    {results.map((c, i) => (
                      <button key={c.clientId}
                        className={`f-res${i === 0 ? " hi" : ""}${c.status === "inactive" ? " off" : ""}`}
                        onClick={() => pick(c)}>
                        <span className="avatar" aria-hidden>{initials(c.name)}</span>
                        <span className="nm">{c.name}</span>
                        <span className="soft">
                          {c.providerName}{c.status === "inactive" ? " · pasif" : ""}
                        </span>
                      </button>
                    ))}
                    <button className="f-res new" onClick={() => onNewClient(query.trim())}>
                      <Icon name="userPlus" /><span>Yeni danışan: “{query.trim()}”</span>
                    </button>
                  </div>
                )}
              </>}
          </Field>
          <Field label="Danışman">
            <ProviderChips providers={providers} value={providerId} onChange={setProviderId} />
          </Field>
          {provider && picked && (needFee || askVaka) && (
            <div className="f-new">
              {needFee && (
                <>
                  <div className="f-label">{provider.name} ile ilk seansı: seans ücreti</div>
                  <FeeInput value={fee} onChange={setFee} autoFocus
                    onEnter={() => focusNext(timeField.current)} />
                </>
              )}
              {askVaka && (
                <Seg two value={vaka} onChange={setVaka} options={[
                  {value: "nefes", label: "Nefes vakası · %50"},
                  {value: "own", label: `${trGenitive(provider.name)} vakası · %40`},
                ]} />
              )}
              <p className={feeBad ? "pay-note bad" : "pay-note"}>
                {feeBad ? "Okunamadı — örnek: 4.000" :
                  `${isOwner ? `${provider.name} kurucu: Nefes payı %0.` :
                    `Nefes payı %${rate}${!isPrimary ? " (ikinci danışmanda vaka Nefes'in)" : ""}.`} ` +
                  (isOwner ? "" : splitWords(feeTL, rate, provider.name, tl))}
              </p>
            </div>
          )}
          <div className="f-row">
            <Field label="Gün">
              <label className="f-input"><Icon name="cal" />
                <input type="date" min={today} value={gun} aria-label="Gün"
                  onChange={(e) => e.target.value && setGun(e.target.value)} />
              </label>
            </Field>
            <Field label="Saat">
              <label className="f-input"><Icon name="clock" />
                <input ref={timeField} type="time" step={300} value={saat} aria-label="Saat"
                  onChange={(e) => setSaat(e.target.value)} />
              </label>
            </Field>
          </div>
          <Field label="Tekrar">
            <Seg options={REPEATS} value={repeat} onChange={setRepeat} />
            {repeatNote && <p className="pay-note">{repeatNote}</p>}
          </Field>
          <button className="pay-go" disabled={!go} onClick={submit}>
            {running ? "Ekleniyor…" : missing ?? <>Seans ekle <span>· {whenShort}</span></>}
          </button>
        </div>
        <div className="pay-strip">
          <div><span>Ücret</span><b>{feeTL !== null ? tl(feeTL) : "—"}</b></div>
          <div><span>Süre</span><b>50 dk</b></div>
          <div><span>Nefes payı</span><b>{picked && provider ? `%${rate}` : "—"}</b></div>
        </div>
      </div>
      <p className="pay-foot">
        Arama bütün kartlara bakar; pasif bir kart seansla birlikte yeniden aktif olur. Her
        hafta ve 2 haftada bir, danışmanın takviminde program açar; programı değiştirmek,
        durdurmak ya da bitirmek sohbette kalır. Kaydettikten hemen sonra çıkan uyarıda Geri al
        durur.
      </p>
    </Sheet>
  );
}

// ── Yeni danışan ──────────────────────────────────────────

const RELATIONS: {value: AddClientInput["guardian"]["relation"]; label: string}[] = [
  {value: "anne", label: "Anne"}, {value: "baba", label: "Baba"}, {value: "diger", label: "Diğer"},
];

// How a confirmation will address the veli (the server's rule): an
// anne by her first name + Hanım, a baba by his + Bey, anyone else
// by the full name.
function greeting(name: string, relation: string): string {
  const tidy = name.trim().replace(/\s+/g, " ");
  if (!tidy) return "";
  const title = relation === "anne" ? "Hanım" : relation === "baba" ? "Bey" : null;
  return title ? `${tidy.split(" ")[0]} ${title}` : tidy;
}

export function AddClientSheet({providers, today, initialName, demo, busy, onBack, onClose,
  onCreate, onCreated, onPickExisting}: {
  providers: BoardProvider[];
  today: string;
  initialName?: string; // what the search had when "Yeni danışan" was tapped
  demo?: DeskClients;
  busy: string | null;
  onBack?: () => void; // back to Seans ekle
  onClose: () => void;
  onCreate: (input: AddClientInput) => Promise<AddClientWire | null>;
  onCreated: (client: DeskClient, guardian: string) => void;
  onPickExisting: (client: DeskClient) => void; // the same name: seans on that card
}) {
  const {tl} = useMoney();
  const list = useDeskClients(demo);
  const owner = list.data?.ownerProviderId ?? null;
  const [name, setName] = useState(initialName ?? "");
  const [dob, setDob] = useState("");
  const [veli, setVeli] = useState("");
  const [phone, setPhone] = useState("");
  const [relation, setRelation] = useState<AddClientInput["guardian"]["relation"]>("anne");
  const [providerId, setProviderId] = useState<string | null>(null);
  const [fee, setFee] = useState("");
  const [vaka, setVaka] = useState<"nefes" | "own">("nefes");
  const nameField = useRef<HTMLInputElement>(null);
  useEffect(() => {
    nameField.current?.focus();
  }, []);

  const tidy = name.trim().replace(/\s+/g, " ");
  const same = useMemo(() => tidy.length < 2 || !list.data ? null :
    list.data.clients.find((c) => fold(c.name) === fold(tidy)) ?? null, [list.data, tidy]);
  const provider = providers.find((p) => p.providerId === providerId) ?? null;
  const isOwner = providerId !== null && providerId === owner;
  const rate = isOwner ? 0 : vaka === "own" ? 40 : 50;
  const feeTL = parseTL(fee);
  const feeBad = fee.trim() !== "" && feeTL === null;
  const missing = same ? "Bu adla kart var" : tidy.length < 2 ? "Adı soyadı girin" :
    veli.trim().length < 2 ? "Veliyi girin" : !phone.trim() ? "Telefonu girin" :
      !provider ? "Danışman seçin" : feeTL === null ? "Ücret girin" : null;
  const running = busy === "add-client";
  const go = missing === null && busy === null;
  const submit = () => {
    if (!go || !provider || feeTL === null) return;
    const input: AddClientInput = {
      name: tidy,
      ...(dob ? {dateOfBirth: dob} : {}),
      guardian: {name: veli.trim(), phone: phone.trim(), relation},
      providerId: provider.providerId, feeTL,
      ...(isOwner ? {} : {vaka}),
    };
    void onCreate(input).then((r) => {
      // The dev demo sends nothing: the card is pretended.
      const made = r ?? (demo ? {
        created: true as const, clientId: `demo-${Date.now()}`, name: tidy, contactId: "demo",
        providerId: provider.providerId, providerName: provider.name, feeTL, rate, warnings: [],
      } : null);
      if (!made) return;
      onCreated({
        clientId: made.clientId, name: made.name, status: "active",
        providerId: made.providerId, providerName: made.providerName,
        terms: {[made.providerId]: {feeTL: made.feeTL, rate: made.rate}},
      }, veli.trim());
    });
  };
  const hello = greeting(veli, relation);

  return (
    <Sheet className="seans-sheet pay-sheet payout-sheet add-sheet" onClose={onClose}
      head={<Head title="Yeni danışan" back={onBack ? "Seans ekle" : undefined} onBack={onBack}
        onClose={onClose} />}>
      <div className="pay-card">
        <div className="pay-main">
          <Field label="Adı soyadı">
            <label className="f-input">
              <input ref={nameField} type="text" autoComplete="off" placeholder="Adı soyadı"
                aria-label="Adı soyadı" value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            {same && (
              <div className="f-warn">
                <span>Bu adla bir kart var: <b>{same.name}</b>
                  {same.providerName ? `, ${trGenitive(same.providerName)} danışanı` : ""}
                  {same.status === "inactive" ? " (pasif)" : ""}. Aynı çocuksa ikinci kart
                  açılmaz, seans o karta eklenir. Aynı adlı başka bir çocuksa kart sohbetten
                  açılır.</span>
                <button className="sd-chip" onClick={() => onPickExisting(same)}>
                  <Icon name="calPlus" />{trDative(same.name)} seans ekle
                </button>
              </div>
            )}
          </Field>
          <Field label="Doğum tarihi" hint="isteğe bağlı" dim={!!same}>
            <label className="f-input"><Icon name="cal" />
              <input type="date" max={today} value={dob} aria-label="Doğum tarihi"
                onChange={(e) => setDob(e.target.value)} />
            </label>
          </Field>
          <div className="f-sep" />
          <div className={same ? "f-row dim" : "f-row"}>
            <Field label="Veli">
              <label className="f-input">
                <input type="text" autoComplete="off" placeholder="Adı soyadı" aria-label="Veli"
                  value={veli} onChange={(e) => setVeli(e.target.value)} />
              </label>
            </Field>
            <Field label="Telefon">
              <label className="f-input">
                <input type="tel" autoComplete="off" placeholder="05__ ___ __ __"
                  aria-label="Telefon" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </label>
            </Field>
          </div>
          <Field dim={!!same}>
            <Seg options={RELATIONS} value={relation} onChange={setRelation} />
            <p className="pay-note">
              {hello ? `WhatsApp onayları bu numaraya, “${hello}” diye gider.` :
                "WhatsApp onayları bu numaraya gider."}
            </p>
          </Field>
        </div>
      </div>
      <div className="pay-card">
        <div className="pay-main">
          <Field label="Danışman" dim={!!same}>
            <ProviderChips providers={providers} value={providerId} onChange={setProviderId} />
          </Field>
          <Field label="Seans ücreti" dim={!!same}>
            <FeeInput value={fee} onChange={setFee} onEnter={submit} />
          </Field>
          <Field label="Kimin vakası" dim={!!same || isOwner}>
            <Seg two value={isOwner ? null : vaka} onChange={setVaka} options={[
              {value: "nefes", label: "Nefes vakası · %50"},
              {value: "own", label: `${provider && !isOwner ? trGenitive(provider.name) : "Danışmanın"} vakası · %40`},
            ]} />
            <p className={feeBad ? "pay-note bad" : "pay-note"}>
              {feeBad ? "Okunamadı — örnek: 4.000" :
                !provider ? "Pay, KB muhasebe kuralına göre: Nefes vakası %50, danışmanın kendi vakası %40." :
                  isOwner ? `${provider.name} kurucu: Nefes payı %0.` :
                    splitWords(feeTL, rate, provider.name, tl) || `Nefes payı %${rate}.`}
            </p>
          </Field>
          <button className="pay-go" disabled={!go} onClick={submit}>
            {running ? "Açılıyor…" : missing ?? <>Kartı aç <span>· sonra ilk seans</span></>}
          </button>
        </div>
      </div>
      <p className="pay-foot">
        TC kimlik, adres ve onaylar kayıt formundan gelir. Faturalı aile, sabit pay ve özel
        düzenlemeler sohbetten girilir.
      </p>
    </Sheet>
  );
}
